import { useState } from 'react';
import useApiRequest from '../../../../lib/hooks/useRequest';
import { useArchiveList } from '../../hooks/use-archive-list';
import { CreateProductAddOnRequestDto, UpdateArchiveRequestDto } from '../../types/request';
import { ProductAddOnDetailsResponseDto, ProductAddOnListItemResponseDto, ProductAddOnResponseDto } from '../../types/response';
import AddOnFormDialog from '../add-on-form-dialog/add-on-form-dialog';
import ArchiveConfirmDialog from '../archive-confirm-dialog/archive-confirm-dialog';
import ListToolbar from '../list-toolbar/list-toolbar';
import AddOnList from './add-on-list';

/** Data owner of the add-ons tab. */
const AddOnListWrapper = () => {
  const { apiRequest } = useApiRequest();
  const { items, pageCount, isLoading, reload, filter, handleFilterChange, pagination, handlePaginationChange } =
    useArchiveList<ProductAddOnListItemResponseDto>('product-add-ons/admin');

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingAddOn, setEditingAddOn] = useState<ProductAddOnDetailsResponseDto | null>(null);
  const [archiveCandidate, setArchiveCandidate] = useState<ProductAddOnListItemResponseDto | null>(null);

  const handleCreate = () => {
    setEditingAddOn(null);
    setIsFormOpen(true);
  };

  const handleEdit = async (item: ProductAddOnListItemResponseDto) => {
    const res = await apiRequest<ProductAddOnDetailsResponseDto>(`product-add-ons/admin/${item._id}`, 'GET');
    if (!res.data) return;
    setEditingAddOn(res.data);
    setIsFormOpen(true);
  };

  const handleSubmit = async (payload: CreateProductAddOnRequestDto): Promise<boolean> => {
    const res = editingAddOn
      ? await apiRequest<ProductAddOnResponseDto>(`product-add-ons/admin/${editingAddOn._id}`, 'PATCH', {
          body: payload,
          toast: { toastText: 'Add-on wurde aktualisiert.' }
        })
      : await apiRequest<ProductAddOnResponseDto>('product-add-ons/admin', 'POST', {
          body: payload,
          toast: { toastText: 'Add-on wurde erstellt.' }
        });
    if (res.error) return false;
    await reload();
    return true;
  };

  const handleArchiveConfirm = async (): Promise<boolean> => {
    if (!archiveCandidate) return false;
    const body: UpdateArchiveRequestDto = { isArchived: !archiveCandidate.isArchived };
    const res = await apiRequest<ProductAddOnResponseDto>(`product-add-ons/admin/${archiveCandidate._id}/archive`, 'PATCH', {
      body,
      toast: { toastText: body.isArchived ? 'Add-on wurde archiviert.' : 'Add-on wurde wiederhergestellt.' }
    });
    if (res.error) return false;
    await reload();
    return true;
  };

  return (
    <div className="flex flex-col gap-5">
      <ListToolbar filter={filter} onFilterChange={handleFilterChange} createLabel="Neues Add-on" onCreate={handleCreate} />

      <AddOnList
        addOns={items}
        filter={filter}
        pagination={pagination}
        pageCount={pageCount}
        onPaginationChange={handlePaginationChange}
        isLoading={isLoading}
        onEdit={handleEdit}
        onArchiveToggle={setArchiveCandidate}
      />

      <AddOnFormDialog isOpen={isFormOpen} onOpenChange={setIsFormOpen} addOn={editingAddOn} onSubmit={handleSubmit} />

      <ArchiveConfirmDialog
        candidate={archiveCandidate}
        entityLabel="Add-on"
        onClose={() => setArchiveCandidate(null)}
        onConfirm={handleArchiveConfirm}
      />
    </div>
  );
};

export default AddOnListWrapper;
